import { atom, PrimitiveAtom } from 'jotai';

type Status = 'notstarted' | 'loading' | 'success' | 'error';

export const activeCabinNoiseGraphTabAtom = atom<number>(0);

export type NullableFloatArray = Array<number | null>;

export type CabinNoiseData = {
    data: {
        cabin_noise_target: NullableFloatArray;
        cabin_noise_pred: NullableFloatArray;
        vel: NullableFloatArray;
        rpm: NullableFloatArray;
    },
    status: Status,
};

export const allConditionDataAtom = atom<Record<string, CabinNoiseData>>({});
export const allConditionLoadingAtom = atom<boolean>(false);

// mtg/powertrain によって mount 系列キーが変わるため、固定キーでは持たない。
export type MountSeriesData = Record<string, NullableFloatArray>;

const createEmptyMountSeriesData = (): MountSeriesData => ({});

export type CabinNoisePredBackgroundData = {
    data: {
        cabin_noise_target: NullableFloatArray;
        cabin_noise_pred: NullableFloatArray;
        background_noise_target: NullableFloatArray;
        deltaL_target: NullableFloatArray;
    },
    status: Status,
};

export const cabinNoisePredBackgroundAtom = atom<CabinNoisePredBackgroundData>({
    data: {
        cabin_noise_target: [],
        cabin_noise_pred: [],
        background_noise_target: [],
        deltaL_target: [],
    },
    status: "notstarted"
});

export type ContributionData = {
    data: MountSeriesData,
    status: Status
};

export const contributionAtom = atom<ContributionData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type NtfData = {
    data: MountSeriesData,
    status: Status
};

export const NtfAtom = atom<NtfData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type MtgInsuData = {
    data: MountSeriesData,
    status: Status
};

export const MtgInsuAtom = atom<MtgInsuData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type MtgVibCtrData = {
    data: MountSeriesData,
    status: Status
};

export const MtgVibCtrAtom = atom<MtgVibCtrData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type MtgVibData = {
    data: MountSeriesData,
    status: Status
};

export const MtgVibAtom = atom<MtgVibData>({
    data: createEmptyMountSeriesData(),
    status: "notstarted"
});

export type EachConditionDataKeys =
    | 'cabin_noise_pred_background'
    | 'contribution'
    | 'ntf'
    | 'mtg_insu'
    | 'mtg_vib_ctr'
    | 'mtg_vib';

export type EachConditionData = {
    graph_id: EachConditionDataKeys;
    data: any;
    status: Status;
};

export const eachConditionDataAtomMap: Record<EachConditionDataKeys, PrimitiveAtom<{ data: any; status: Status }>> = {
    cabin_noise_pred_background: cabinNoisePredBackgroundAtom,
    contribution: contributionAtom,
    ntf: NtfAtom,
    mtg_insu: MtgInsuAtom,
    mtg_vib_ctr: MtgVibCtrAtom,
    mtg_vib: MtgVibAtom,
};

export const setEachConditionDataAtom = atom<null, EachConditionData[], void>(
    null,
    (_get, set, ...payload: EachConditionData[]) => {
        payload.forEach(({ graph_id, data, status }) => {
            set(eachConditionDataAtomMap[graph_id], { data, status });
        })
    }
);

export const getEachConditionDataAtom = atom<EachConditionData[]>(get => {
    return (Object.keys(eachConditionDataAtomMap) as EachConditionDataKeys[]).map((key) => {
        const { data, status } = get(eachConditionDataAtomMap[key])
        return { graph_id: key, data, status }
    })
});

export const allEachConditionDataErrorAtom = atom<boolean>(get => {
    const list = get(getEachConditionDataAtom)
    return list.every(item => item.status === 'error')
});

export const eachConditionDataByKeyAtom = (key: EachConditionDataKeys) =>
    atom(get => {
        const list = get(getEachConditionDataAtom)
        return list.find((item): item is EachConditionData => item.graph_id === key)
    });
